import { QueryClient, QueryClientConfig } from '@tanstack/react-query';
import { isApiError, ApiError } from './errors';

/**
 * Determine if an error should trigger a retry
 *
 * Decision tree:
 * - 4xx Client Errors → NO RETRY (client error)
 * - 5xx Server Errors → RETRY (server error)
 * - Network Errors → RETRY (transient)
 * - Timeout → RETRY (up to max)
 * - Max 3 retries total
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
    // Max 3 retries
    if (failureCount >= 3) {
        return false;
    }

    // Check if it's our structured ApiError
    if (isApiError(error)) {
        const apiError = error as ApiError;

        // Never retry client errors (4xx)
        if (apiError.status >= 400 && apiError.status < 500) {
            return false;
        }

        // Retry server errors (5xx)
        if (apiError.status >= 500) {
            return true;
        }

        // Retry network errors (timeout, no connection)
        if (apiError.isNetworkError) {
            return true;
        }
    }

    // For unknown errors, retry up to 2 times
    return failureCount < 2;
}

/**
 * Calculate retry delay with exponential backoff
 *
 * 1st retry: 1000ms (1 second)
 * 2nd retry: 2000ms (2 seconds)
 * 3rd retry: 4000ms (4 seconds)
 * Max: 8000ms (8 seconds)
 */
export function getRetryDelay(attemptIndex: number): number {
    return Math.min(1000 * Math.pow(2, attemptIndex), 8000);
}

/**
 * Default query client configuration
 *
 * Provides sensible defaults for:
 * - Retry logic based on error type
 * - Exponential backoff between retries
 * - Cache timing and revalidation
 */
const queryClientConfig: QueryClientConfig = {
    defaultOptions: {
        queries: {
            // Data stays fresh for 5 minutes
            staleTime: 1000 * 60 * 5,

            // Keep data in cache for 30 minutes (renamed from cacheTime in v5)
            gcTime: 1000 * 60 * 30,

            // Custom retry logic based on error type
            retry: shouldRetry,
            retryDelay: getRetryDelay,

            // Don't refetch on window focus (can be annoying)
            refetchOnWindowFocus: false,

            // Don't refetch on mount if data is fresh
            refetchOnMount: false,

            // Refetch when reconnecting to internet
            refetchOnReconnect: true,
        },
        mutations: {
            // Don't retry mutations by default (could cause duplicate submissions)
            // For idempotent mutations, enable retry explicitly per-mutation
            retry: false,
        },
    },
};

/**
 * Create a new configured query client instance
 *
 * Use this factory function if you need multiple query clients
 * (e.g., for testing or server-side rendering)
 */
export function createQueryClient(): QueryClient {
    return new QueryClient(queryClientConfig);
}

/**
 * Default query client instance for the application
 *
 * This is the primary query client used by the app.
 * Import this in App.tsx for the QueryClientProvider.
 */
export const queryClient = createQueryClient();

export default queryClient;
